/** PROTOTYPE project settings -- THROWAWAY. Project-level welding services + rate-card defaults spreads inherit. */
import React, { useState } from 'react';
import { CRC } from './moduleInputsData';
import { Label, Segmented, Toggle } from './protoAtoms';
import {
  ProjectState, ModuleKey, MODULE_LABELS, TrainingTier, WeldingServices,
  money, projectCfg, projectCfgOverridden, moduleConstDefault,
} from './spreadSplitData';
import { ServicesPriceState } from './useServicesPrice';
import { ConfigDrawer } from './SpreadOverrideDrawer';

const MODULE_ORDER: ModuleKey[] = ['welding', 'mt', 'ndt', 'coating', 'platform', 'retention'];

const card: React.CSSProperties = { background: '#fff', border: `1px solid ${CRC.border}`, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: 20, marginBottom: 18 };
const numBox: React.CSSProperties = { width: 90, padding: '8px 10px', fontFamily: 'Funnel Sans', fontSize: 14, fontWeight: 600, color: CRC.blueprintBlue, border: `1px solid ${CRC.border}`, borderRadius: 8 };

interface Props { project: ProjectState; setProject: (p: ProjectState) => void; services: ServicesPriceState; }

export const ProjectSettings: React.FC<Props> = ({ project, setProject, services }) => {
  const [cardModule, setCardModule] = useState<ModuleKey | null>(null);

  const patchServices = (patch: Partial<WeldingServices>) => setProject({ ...project, services: { ...project.services, ...patch } });
  const patchProjectCfg = (module: ModuleKey, key: string, v: number | undefined) => {
    const modCfg: Record<string, number> = { ...(project.config?.[module]) };
    if (v === undefined) delete modCfg[key]; else modCfg[key] = v;
    setProject({ ...project, config: { ...project.config, [module]: modCfg } });
  };
  const resetProjectModule = (module: ModuleKey) => setProject({ ...project, config: { ...project.config, [module]: {} } });

  const sv = project.services;
  // Real services total from the engine (billed once); "…" while pricing, "Off" when disabled.
  const svDisplay = !sv.enabled ? 'Off' : services.status === 'done' ? money(services.result.total) : services.status === 'error' ? '—' : '…';
  const overrideCount = (m: ModuleKey) => Object.keys(project.config?.[m] ?? {}).length;

  const numField = (label: string, value: number, onChange: (v: number) => void) => (
    <div><Label>{label}</Label><div style={{ marginTop: 4 }}>
      <input title={label} type="number" value={value} onChange={(e) => onChange(Math.max(0, Number(e.target.value)))} style={numBox} />
    </div></div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ fontFamily: 'Funnel Sans', fontSize: 18, fontWeight: 700, color: CRC.blueprintBlue, marginBottom: 4 }}>Project settings</div>
      <div style={{ fontSize: 12.5, color: CRC.heritageGrey, marginBottom: 18 }}>Configured once for the whole project. Every spread inherits the rate cards; the platform services are billed once, not per spread.</div>

      {/* Platform services */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue }}>Platform services (billed once)</div>
            <div style={{ fontSize: 12, color: CRC.heritageGrey, marginTop: 2 }}>Support, training, provisioning and on-field. Charged once for the whole project, not per spread.</div>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Toggle on={sv.enabled} onClick={() => patchServices({ enabled: !sv.enabled })} label={sv.enabled ? 'Included' : 'Not included'} />
            <div style={{ textAlign: 'right' }}>
              <Label>Services total</Label>
              <div style={{ fontFamily: 'Funnel Sans', fontSize: 20, fontWeight: 700, color: CRC.readableRed }}>{svDisplay}</div>
            </div>
          </div>
        </div>
        {sv.enabled ? (
          <>
            <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginTop: 16, alignItems: 'flex-end' }}>
              {numField('Support hours', sv.supportHours, (v) => patchServices({ supportHours: v }))}
              <div><Label>Training</Label><div style={{ marginTop: 4 }}>
                <Segmented value={sv.trainingTier} onChange={(v) => patchServices({ trainingTier: v as TrainingTier })}
                  options={[{ value: 'none', label: 'None' }, { value: 'quick', label: 'Quick' }, { value: 'medium', label: 'Medium' }, { value: 'long', label: 'Long' }]} />
              </div></div>
              {numField('Provisioning hours', sv.provisioningHours, (v) => patchServices({ provisioningHours: v }))}
              {numField('On-field days', sv.onFieldDays, (v) => patchServices({ onFieldDays: v }))}
            </div>
            <div style={{ fontSize: 11, color: CRC.heritageGrey, marginTop: 12 }}>Priced once by the engine at cost (services carry no margin). Internet &amp; cloud stays per spread (it follows each spread's machines).</div>
          </>
        ) : (
          <div style={{ fontSize: 12.5, color: CRC.textGrey, marginTop: 14 }}>Platform services are not included. Toggle <strong>Included</strong> to add them (settings are kept).</div>
        )}
      </div>

      {/* Project rate cards */}
      <div style={card}>
        <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue, marginBottom: 4 }}>Project rate cards</div>
        <div style={{ fontSize: 12, color: CRC.heritageGrey, marginBottom: 12 }}>Set each module's rate card once. Spreads inherit these; a spread only shows what it overrides.</div>
        {MODULE_ORDER.map((m) => {
          const n = overrideCount(m);
          return (
            <div key={m} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 4px', borderBottom: `1px solid ${CRC.rowDivider}` }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: CRC.blueprintBlue }}>{MODULE_LABELS[m]}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {n > 0 && <span style={{ fontSize: 11, color: CRC.readableRed, fontWeight: 600 }}>{n} changed from default</span>}
                <button onClick={() => setCardModule(m)} style={{ padding: '6px 12px', border: `1px solid ${CRC.border}`, borderRadius: 8, background: '#fff', color: CRC.readableRed, fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>Edit rate card</button>
              </div>
            </div>
          );
        })}
      </div>

      {cardModule && (
        <ConfigDrawer
          title={`Project · ${MODULE_LABELS[cardModule]} rate card`}
          subtitle="The project baseline every spread inherits. Edit any value to change it project-wide."
          moduleKey={cardModule}
          band={null}
          getValue={(k) => projectCfg(project, cardModule, k)}
          getDefault={(k) => moduleConstDefault(cardModule, k)}
          isOverridden={(k) => projectCfgOverridden(project, cardModule, k)}
          setCfg={(k, v) => patchProjectCfg(cardModule, k, v)}
          resetAll={() => resetProjectModule(cardModule)}
          resetLabel={`Reset ${MODULE_LABELS[cardModule]} to defaults`}
          onClose={() => setCardModule(null)} />
      )}
    </div>
  );
};
